// CoherenceBadge (VW-MIG-V2.04 / CFR-VW-145) — the consumer "stale" signal. Shows an amber marker
// when the app has N stale subscriptions (a BoM/asset it ran on advanced past what it last used).
// Renders nothing when fresh (staleCount 0) — zero chrome for current apps. Presentational only; the
// data comes from useAppCoherence, and the recompute/rerun action is the consuming app's own wiring.
//
// T1 drill-in: the count is shown inline, and the changed assets are listed in the title (tooltip) —
// making the built fan-out LEGIBLE (which assets went stale, at what pinned version) without a
// click-popover that would fight the enclosing chip's own click. Read-only by design.
import type { StaleSubscription } from "../state/useAppCoherence";

function drillInTitle(staleCount: number, stale: StaleSubscription[]): string {
  const head = `${staleCount} subscribed asset${staleCount > 1 ? "s" : ""} changed — recompute needed`;
  if (stale.length === 0) return head; // count known but worklist not passed (back-compat)
  const lines = stale.map((s) => {
    const ver = s.pinned_version != null ? ` (pinned v${s.pinned_version})` : "";
    const cls = s.info_class ? ` · ${s.info_class}` : "";
    return `• ${s.asset_kind} ${s.asset_id}${ver}${cls}`;
  });
  return `${head}:\n${lines.join("\n")}`;
}

export function CoherenceBadge({
  staleCount,
  stale = [],
}: {
  staleCount: number;
  stale?: StaleSubscription[];
}): React.ReactElement | null {
  if (staleCount <= 0) return null;
  const title = drillInTitle(staleCount, stale);
  return (
    <span
      data-app="coherence-badge"
      role="status"
      aria-label={title}
      title={title}
      className="inline-flex items-center gap-1 rounded-full border border-[var(--color-warning-50,#FFC000)] bg-[var(--color-warning-15,rgba(255,192,0,0.12))] px-1.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-warning-60,#FFC000)]"
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[var(--color-warning-50,#FFC000)]" />
      {staleCount} stale
    </span>
  );
}
